import { useCallback, useMemo, useState } from 'react';
import { message, Modal } from 'antd';
import {
  ExclamationCircleOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpMasterShell,
  useErpMasterQuery
} from '../../../../components/common/erp-grid';
import { buildCustomerColumns } from './customerColumns';
import { customerApi } from './customerApi';
import CustomerTabbedDrawer from './CustomerTabbedDrawer';
import CustomerMasterHeader from './CustomerMasterHeader';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import './CustomerMasterHeader.css';

// Stage 6A.2-UX v2 — the filter chips are a filter entry-point. Each maps
// to a grid query filter patch; clicking the active chip again (or "All")
// clears the filter. GST = every registration type EXCEPT unregistered /
// consumer; URP = the inverse; Over-limit is a server flag the list endpoint
// honours (customers whose live receivable exceeds their credit limit).
const GST_REGISTERED_TYPES = [
  'registered_regular',
  'registered_composition',
  'sez',
  'export',
  'deemed_export'
];
const URP_TYPES = ['unregistered', 'consumer'];
const CHIP_FILTER = {
  all:     [],
  active:  [{ field: 'status', value: 'active',  op: 'equals' }],
  blocked: [{ field: 'status', value: 'blocked', op: 'equals' }],
  gst:     [{ field: 'gst_registration_type', value: GST_REGISTERED_TYPES, op: 'in' }],
  urp:     [{ field: 'gst_registration_type', value: URP_TYPES, op: 'in' }]
};

// ─────────────────────────────────────────────────────────────────
// Glass-i — Customer Master page (Stage 6A.2 → 6A.2B).
//
// Stage 6A.2 wired the reusable ErpDataGrid against the tenant
// backend with the grid's BUILT-IN modal as the Add/Edit/View
// surface.
//
// Stage 6A.2B replaces that built-in modal with a dedicated tabbed
// Drawer (`CustomerTabbedDrawer`) for richer GSTIN-search + nested
// addresses/contacts/wastage. We don't redesign the grid internals
// — we OVERRIDE `onAdd` / `onEdit` / `onView` so the grid hands off
// to our Drawer instead of opening its modal. Inline edit / delete
// / cell PATCH / list still use the grid's stock paths.
// ─────────────────────────────────────────────────────────────────

const ADAPTER = {
  list: (query) => customerApi.list(query)
};

function defaultOnError(err) {
  const msg = err?.response?.data?.error?.message ||
    err?.message ||
    'Failed to load customers.';
  message.error({ content: msg, key: 'customer-list' });
}

export default function CustomerMasterPage() {
  const { gridHandlers, refetch, query, summary, patchQuery } = useErpMasterQuery({
    adapter: ADAPTER,
    pageSize: 25,
    onError: defaultOnError
  });

  // Stage 6A.2-UX v2 — active filter chip + click handler. The chip applies a
  // status / GST filter to the grid query (page reset to 1). Chip counts come
  // from summary.totals / summary.exposure (unfiltered) so they stay stable.
  const [activeChip, setActiveChip] = useState('all');
  const onChipClick = useCallback((key) => {
    const next = CHIP_FILTER[key] ? key : 'all';
    setActiveChip(next);
    patchQuery({ filters: CHIP_FILTER[next] || [], page: 1 });
  }, [patchQuery]);

  // Command-bar search drives the SAME grid query as the (hidden) grid search.
  const searchValue = (query && query.search) || '';
  const onSearchChange = useCallback((v) => {
    patchQuery({ search: v || '', page: 1 });
  }, [patchQuery]);

  const tileTotals = summary && summary.totals ? summary.totals : null;
  const exposure   = summary && summary.exposure ? summary.exposure : null;

  // Stage 6A.2I — S.No column is rendered with pagination-aware
  // indexing. The grid passes (value, row, page-relative idx) to a
  // column's render, so we close over the current page + pageSize
  // here and re-build the column tree whenever either changes.
  const currentPage = query && query.page     ? query.page     : 1;
  const pageSize    = query && query.pageSize ? query.pageSize : 25;
  const columns = useMemo(
    () => buildCustomerColumns({ currentPage, pageSize }),
    [currentPage, pageSize]
  );

  // Stage 6A.2D — Approval MFA hook. The modal slot renders just
  // below the grid; the hook drives create-challenge → verify-L1 →
  // run-action → consume in sequence.
  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  // Drawer state. `mode` is 'add' | 'edit' | 'view'.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add');
  const [drawerRecord, setDrawerRecord] = useState(null);

  const openAdd  = useCallback(() => {
    setDrawerMode('add'); setDrawerRecord(null); setDrawerOpen(true);
  }, []);
  const openEdit = useCallback((record) => {
    setDrawerMode('edit'); setDrawerRecord(record); setDrawerOpen(true);
  }, []);
  const openView = useCallback((record) => {
    setDrawerMode('view'); setDrawerRecord(record); setDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const onSaved = useCallback(async () => {
    setDrawerOpen(false);
    await refetch();
  }, [refetch]);

  // Stage 6A.2H — Sequenced confirmation → Approval MFA. The previous
  // implementation kicked off `runWithApproval` from inside Modal.confirm's
  // `onOk` callback. Because `onOk` returned a pending promise, AntD kept
  // the confirmation modal mounted (with its mask) until the entire MFA
  // flow resolved, so both modals stacked. We now resolve the confirm
  // promise from `afterClose` — guaranteeing the confirmation is fully
  // unmounted before the Approval MFA modal opens.
  const handleDelete = useCallback(
    async (record) => {
      const confirmed = await new Promise((resolve) => {
        let okClicked = false;
        Modal.confirm({
          title: 'Delete customer?',
          icon: <ExclamationCircleOutlined />,
          content: `${record.customerCode} · ${record.customerName} will be deactivated and hidden from lists. Posted transactions are preserved. You will be asked for a 6-digit Approval MFA code to confirm.`,
          okText: 'Delete',
          okButtonProps: { danger: true },
          cancelText: 'Cancel',
          onOk: () => { okClicked = true; },
          onCancel: () => { okClicked = false; },
          afterClose: () => resolve(okClicked)
        });
      });
      if (!confirmed) return;

      try {
        // Stage 6A.2D — Approval MFA L1 step-up. The hook creates
        // a server-side challenge, opens the modal, verifies the
        // code, then re-fires the protected DELETE with the
        // challenge id attached. Backend consumes the challenge
        // only after the soft delete succeeds.
        await runWithApproval({
          operationKey: 'customer.delete',
          resourceType: 'customer',
          resourceId:   record.id,
          operationLabel: 'Delete customer',
          resourceLabel:  `${record.customerCode} · ${record.customerName}`,
          action: (approvalMfaChallengeId) =>
            customerApi.remove(record.id, { approvalMfaChallengeId })
        });
        message.success('Customer deleted.');
        await refetch();
      } catch (err) {
        if (err && err.name === 'ApprovalMfaCancelled') return;
        const msg = err?.response?.data?.error?.message ||
          'Failed to delete customer.';
        message.error(msg);
      }
    },
    [refetch, runWithApproval]
  );

  // Stage 6A.2D — wrap status changes through Approval MFA when the
  // target status is non-active. Activation skips MFA (backend gate
  // does the same — see approvalMfaForStatusChange in routes/erpCustomers.js).
  const handleStatusChange = useCallback(
    async (record, status, reason) => {
      const needsApproval = status !== 'active';
      try {
        if (needsApproval) {
          await runWithApproval({
            operationKey: 'customer.deactivate',
            resourceType: 'customer',
            resourceId:   record.id,
            operationLabel:
              status === 'blocked' ? 'Block customer' : 'Deactivate customer',
            resourceLabel:  `${record.customerCode} · ${record.customerName}`,
            action: (approvalMfaChallengeId) =>
              customerApi.updateStatus(
                record.id, { status, reason },
                { approvalMfaChallengeId }
              )
          });
        } else {
          await customerApi.updateStatus(record.id, { status, reason });
        }
        message.success('Customer status updated.');
        await refetch();
      } catch (err) {
        if (err && err.name === 'ApprovalMfaCancelled') return;
        const msg = err?.response?.data?.error?.message ||
          'Failed to update status.';
        message.error(msg);
      }
    },
    [refetch, runWithApproval]
  );

  const handleCellUpdate = useCallback(
    async (record, field, value) => {
      const fieldMap = {
        creditLimit: 'credit_limit',
        creditDays: 'credit_days',
        status: 'status'
      };
      const backendField = fieldMap[field];
      if (!backendField) {
        message.error('Field is not editable inline.');
        throw new Error('inline edit not supported');
      }
      try {
        await customerApi.updateCell(record.id, { field: backendField, value });
        message.success('Updated.', 1.5);
        await refetch();
      } catch (err) {
        const msg = err?.response?.data?.error?.message ||
          'Failed to update.';
        message.error(msg);
        throw err;
      }
    },
    [refetch]
  );

  // Stage 6A.2-UX v2 — Add now lives in the command bar, so the grid's own
  // toolbar Add button is disabled here to avoid a duplicate. View / Edit /
  // Delete row actions are unchanged.
  const actionsProp = useMemo(
    () => ({ add: false, view: true, edit: true, delete: true }),
    []
  );
  const pageSizeOptions = useMemo(() => ['15', '25', '50', '100'], []);

  // Stage 6A.2G — Reusable right-click context menu. Reuses the SAME
  // handlers as the action-column buttons (openView / openEdit /
  // handleDelete) so the Approval-MFA-protected delete flow is shared.
  // Future masters can copy this shape; see docs/24-erp-grid-developer-guide.md.
  const contextMenuActions = useMemo(() => ([
    {
      key:   'view',
      label: 'View',
      icon:  <EyeOutlined />,
      onClick: (record) => openView(record)
    },
    {
      key:   'edit',
      label: 'Edit',
      icon:  <EditOutlined />,
      onClick: (record) => openEdit(record)
    },
    {
      key:    'delete',
      label:  'Delete',
      icon:   <DeleteOutlined />,
      danger: true,
      onClick: (record) => handleDelete(record)
    }
  ]), [openView, openEdit, handleDelete]);

  return (
    <ErpMasterShell>
      <CustomerMasterHeader
        totals={tileTotals}
        exposure={exposure}
        activeChip={activeChip}
        onChipClick={onChipClick}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        onAdd={openAdd}
        loading={gridHandlers.loading && !tileTotals}
      />
      <ErpDataGrid
        /* Stage 6A.2-UX — the page title now lives in CustomerMasterHeader
           (the masthead above), so the grid's own toolbar title is omitted
           to avoid a duplicate heading. The toolbar keeps search + actions. */
        /* Stage 6A.2K-fix — bumped gridId from "customer-master" so any
           operator who reordered columns earlier (and thus had a stale
           columnOrder persisted in localStorage that placed City after
           Credit) gets a fresh hydrate matching the current source-of-
           truth column order in customerColumns.jsx. The old key stays
           on disk but is never read again; the next time the grid mounts
           it stores fresh prefs under the new key. */
        /* Bumped v2 → v3 so the new leading S.No column hydrates first for
           everyone — a persisted columnOrder from a pre-S.No session lacks
           `serialNo` and would otherwise sort it to the end. The old key is
           abandoned on disk; a fresh order is stored under the new key. */
        gridId="customer-master-v3"
        /* Stage 6A.2-UX v2 — professional grid skin. Scoped `.cmx-grid`
           overrides (zebra, selection rail, mono/chip/money cells) live
           in CustomerMasterHeader.css and never touch the shared
           `.erp-grid` baseline used by /invoices and /users. */
        className="cmx-grid"
        columns={columns}
        rowKey="id"
        /* Stage 6A.2K-fix — tint blocked customers with the rose theme
           that the Status pill already uses, so the row reads as
           "blocked" at a glance without the operator having to scan
           the status cell. The class is consumed by CSS in index.css. */
        rowClassName={(record) =>
          record && record.status === 'blocked' ? 'erp-grid__row--blocked' : ''
        }
        rowHeight="compact"
        stickyHeader
        selectable
        actionWidth={96}
        pageSizeOptions={pageSizeOptions}
        /* Stage 6A.2-UX v2 — search moved to the command bar (drives the same
           grid query via onQueryChange); the grid's built-in search is hidden
           to avoid a duplicate box. Export / columns / filters / group stay in
           the grid toolbar. */
        searchable={false}
        exportable
        exportFilename="customer-master"
        actions={actionsProp}
        emptyTitle="No customers yet"
        emptyText="Add your first customer to start invoicing, dispatching and tracking credit."
        emptyCtaLabel="Add Customer"
        // Stage 6A.2B — hand off to our tabbed Drawer. Passing
        // `onAdd` / `onEdit` / `onView` makes the grid skip its
        // built-in modal entirely.
        onAdd={openAdd}
        onEdit={openEdit}
        onView={openView}
        // Stage 6A.2E — double-click row opens View popup. The reusable
        // grid wires double-click → onRowClick(record) when the prop is
        // present (`isInteractiveRowClick` already filters out clicks on
        // the checkbox / action ⋯ menu / embedded buttons so this
        // doesn't fight row-selection or the inline action menu).
        onRowClick={openView}
        onDelete={handleDelete}
        onUpdateCell={handleCellUpdate}
        // Stage 6A.2G — right-click context menu (View / Edit / Delete).
        contextMenuActions={contextMenuActions}
        {...gridHandlers}
      />
      <CustomerTabbedDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={drawerRecord}
        onClose={closeDrawer}
        onSaved={onSaved}
        runWithApproval={runWithApproval}
      />
      {/* Stage 6A.2D — Approval MFA modal slot. Renders once at the
          page level; the hook controls open/close state. */}
      {ApprovalMfaModalSlot}
    </ErpMasterShell>
  );
}
